import { UserGamificationDetailsContextType } from './user-gamification-details-context.type';
import { createContext, ReactNode, useContext } from 'react';
import { Outlet } from 'react-router-dom';
import { useUserGamificationDetails } from 'src/hooks/use-user-gamification-details';
import i18n from 'src/i18n';

// Create the user stats context with an initial undefined value.
const UserGamificationDetailsContext = createContext<UserGamificationDetailsContextType | undefined>(undefined);

/**
 * UserStatsProvider component wraps the application and provides user stats context.
 * @param children - The components that will have access to the user stats context.
 */
export function UserGamificationDetailsProvider({ children }: { children?: ReactNode }) {
  const userGamificationDetails = useUserGamificationDetails(); // Retrieve user stats state and methods from the custom hook.

  // Provide the user stats context to the component tree.
  return (
    <UserGamificationDetailsContext.Provider value={userGamificationDetails}>
      {children || <Outlet />}
    </UserGamificationDetailsContext.Provider>
  );
}

/**
 * Custom hook to access the user stats context.
 * Throws an error if used outside of an UserStatsProvider.
 */
export function useUserGamificationDetailsContext() {
  const context = useContext(UserGamificationDetailsContext); // Access the user stats context.

  // Throw an error if the hook is used outside of an UserStatsProvider.
  if (!context) {
    throw new Error(i18n.t('ERRORS.CONTEXT.USER_GAMIFICATION_DETAILS'));
  }

  return context; // Return the context value if it exists.
}
